/**
 * /terms — pilot-grade Terms of Service. Pairs with /privacy so the
 * lead-capture forms can reference both per GDPR + the standard "by
 * continuing you agree to…" pattern.
 *
 * Drafted for the pilot phase: clear language, no exotic clauses.
 * Production should layer a lawyer-reviewed MSA on top of this once
 * the first enterprise contract is signed.
 */
import { Helmet } from "react-helmet-async";
import { PublicShell } from "@/components/PublicShell";
import { Seo } from "@/components/Seo";

const UPDATED = "18 June 2026";

const SECTIONS = [
  {
    h: "1. Acceptance",
    p: ("These Terms govern your access to the ERIO platform (the \u201cService\u201d) operated by " +
        "Ekselens Consulting Limited (\u201cEkselens\u201d, \u201cwe\u201d). By creating an account, " +
        "accessing a pilot or live demo, or otherwise using the Service, you agree to be bound by " +
        "these Terms. If you do not agree, do not use the Service."),
  },
  {
    h: "2. Pilot / preview status",
    p: ("ERIO is in active pilot. The Service is provided \u201cas-is\u201d during this phase; data " +
        "shown may include synthetic content for demonstration purposes. Production-grade SLAs are " +
        "available only under a separately executed Master Services Agreement (\u201cMSA\u201d)."),
  },
  {
    h: "3. Your account & access",
    p: ("You are responsible for safeguarding your credentials and any activity under your account. " +
        "Access is granted to authorised users only. We may suspend or terminate access for misuse, " +
        "security risk, or breach of these Terms."),
  },
  {
    h: "4. Acceptable use",
    p: ("You agree not to: reverse-engineer, copy or resell the Service; submit malicious code; use " +
        "the Service to violate law or third-party rights; or attempt to extract aggregate data on " +
        "other tenants. You retain ownership of data you upload; we retain ownership of the " +
        "platform and any anonymised, aggregate analytics derived from operating it."),
  },
  {
    h: "5. Confidentiality",
    p: ("Materials marked confidential — including platform internals, benchmark methodology, " +
        "AI prompts, and pilot pricing — remain Ekselens\u2019 confidential information. You agree " +
        "not to disclose them outside your organisation without our written consent."),
  },
  {
    h: "6. Data protection",
    p: ("Our handling of personal data is governed by our Privacy Policy (link below). Where you " +
        "process personal data of EU/UK data subjects through the Service, a Data Processing " +
        "Agreement (DPA) will be executed alongside the MSA. Lead-capture forms collect only the " +
        "fields you choose to provide and are retained per the Privacy Policy."),
  },
  {
    h: "7. Intellectual property",
    p: ("The Service, all underlying methodologies (including UK patent application GB2613313.2) and " +
        "all branding remain the property of Ekselens. No licence is granted to you except as " +
        "expressly stated in these Terms or a written MSA."),
  },
  {
    h: "8. Disclaimer of warranties",
    p: ("To the maximum extent permitted by law, the Service is provided without warranties of any " +
        "kind, whether express or implied. ERIO outputs are decision-support; final risk decisions " +
        "remain the responsibility of your organisation and its officers."),
  },
  {
    h: "9. Limitation of liability",
    p: ("Except for liability that cannot lawfully be limited, neither party shall be liable for " +
        "indirect, special, consequential or punitive damages. Our total aggregate liability arising " +
        "from or related to the Service shall not exceed the fees paid (or, during pilot, GBP " +
        "10,000)."),
  },
  {
    h: "10. Termination",
    p: ("Either party may terminate access on written notice. On termination, your data may be " +
        "exported via the offboarding bundle for up to 30 days; thereafter it is deleted in line " +
        "with our retention policy."),
  },
  {
    h: "11. Governing law",
    p: ("These Terms are governed by the laws of England and Wales. Disputes shall be resolved in " +
        "the courts of England and Wales, save that we may seek injunctive relief in any competent " +
        "jurisdiction."),
  },
  {
    h: "12. Changes",
    p: ("We may update these Terms from time to time. Material changes will be communicated to " +
        "active account holders by email or in-product banner before they take effect."),
  },
  {
    h: "13. Contact",
    p: ("Questions about these Terms can be sent to info@ekselens.com. We aim to respond within " +
        "five working days."),
  },
];

export default function Terms() {
  return (
    <PublicShell>
      <Seo
        title="Terms of Service"
        description="Terms of Service for the ERIO platform during the pilot phase — acceptable use, IP, liability, and termination clauses."
        path="/terms"
      />
      <Helmet>
        <title>Terms of Service — ERIO</title>
      </Helmet>
      <article
        className="mx-auto max-w-3xl px-6 py-12"
        data-testid="terms-page"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ek-purple">
          ERIO Programme
        </p>
        <h1 className="mt-2 font-display text-4xl font-medium tracking-[-0.025em] text-ek-ink sm:text-5xl">
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-ek-ink/55">Last updated {UPDATED}</p>

        <div className="prose prose-slate mt-8 max-w-none">
          {SECTIONS.map((s) => (
            <section key={s.h} className="mb-6" data-testid={`terms-section-${s.h.split(".")[0]}`}>
              <h2 className="font-display text-xl font-semibold text-ek-ink">{s.h}</h2>
              <p className="mt-2 text-[15px] font-light leading-relaxed text-ek-ink/75">{s.p}</p>
            </section>
          ))}
          <section className="mb-6 rounded-xl bg-slate-50 p-4 text-xs text-ek-ink/60">
            See also: <a href="/privacy" className="font-medium text-ek-purple hover:underline">Privacy Policy</a> &middot;{" "}
            <a href="/trust" className="font-medium text-ek-purple hover:underline">Trust Centre</a>
          </section>
        </div>
      </article>
    </PublicShell>
  );
}
